import { useEffect, useRef, type ReactNode } from "react";
import { BackIcon, CloseIcon, ForwardIcon } from "../ui/icons";

/**
 * The three shapes an overlay can take, and nothing else.
 *
 * A panel is a whole side of the shell (settings, billing). A sheet is a card
 * over the middle of it (share, the takedown ceremony, a new knock). A dialog
 * is a sheet that is asking a question and will not be scrolled past (the
 * paywall). They differ in size and in where they come from; they are identical
 * in every other respect, which is why they are one file.
 *
 * NONE OF THEM LISTENS FOR ESCAPE. The shell does, once, because only the shell
 * can see which of these is in front (`state.tsx`). An overlay that closed
 * itself on Escape would close at the same time as the dialog on top of it.
 *
 * A PANEL'S GROUND IS `sidebar`, NOT `surface` (reference build, 2026-08-25).
 * The two roles carry the same value in light (#f2f2f7) and in dark (#1c1c1e),
 * so this is a no-op there and the change is entirely about the skin: `surface`
 * in nob mode is the pale cream a card is printed on, and a panel opening in
 * cream reads as a sheet of paper rather than as a wall of the room. `sidebar`
 * is the column colour — the strong yellow — which is what the reference build
 * opens both of its panels on, and it is what makes a `bg-bg` plate inside read
 * as an object standing ON something rather than as a slightly different white.
 */

/** Focus moves into the overlay when it opens, and back when it closes. */
function useFocusTrapEntry(): (node: HTMLElement | null) => void {
  const opener = useRef<Element | null>(null);
  useEffect(() => {
    opener.current = document.activeElement;
    return () => {
      const back = opener.current;
      if (back instanceof HTMLElement && document.contains(back)) back.focus();
    };
  }, []);
  return (node: HTMLElement | null) => {
    if (!node) return;
    // The panel itself, not its first control: a settings panel that stole
    // focus into a text field would start every visit mid-edit.
    if (!node.contains(document.activeElement)) node.focus();
  };
}

function Scrim({ onClose, label }: { onClose: () => void; label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      tabIndex={-1}
      onClick={onClose}
      className="absolute inset-0 cursor-default bg-ink/40"
    />
  );
}

export function SidePanel({
  title,
  label,
  onBack,
  onClose,
  wide = false,
  children,
}: {
  title: string;
  /** What the panel IS, for a screen reader. Stable across its pages. */
  label: string;
  /**
   * Back, when the panel has an inside. `null` on the index page, where the
   * same control is the way out — one chevron doing one thing at a time.
   */
  onBack: (() => void) | null;
  onClose: () => void;
  wide?: boolean;
  children: ReactNode;
}) {
  const focus = useFocusTrapEntry();
  return (
    <div className="nob-scrim fixed inset-0 z-40 flex justify-start">
      <Scrim onClose={onClose} label={`Close ${label.toLowerCase()}`} />
      <aside
        ref={focus}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        data-panel
        className={`nob-panel-in relative flex h-full w-full flex-col bg-sidebar text-text
                    shadow-xl outline-none ${wide ? "max-w-[34rem]" : "max-w-[380px]"}`}
      >
        <header className="flex h-14 shrink-0 items-center gap-1 border-b border-border px-2">
          <button
            type="button"
            aria-label={onBack ? "Back" : `Close ${label.toLowerCase()}`}
            onClick={onBack ?? onClose}
            className="grid size-9 shrink-0 place-items-center rounded-full text-muted
                       transition-colors duration-75 hover:bg-hover hover:text-text
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70"
          >
            <BackIcon className="h-5 w-5" />
          </button>
          <h2 className="min-w-0 flex-1 truncate text-[16px] font-semibold tracking-tight">
            {title}
          </h2>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </aside>
    </div>
  );
}

/**
 * A card over the middle of the shell.
 *
 * THE MEASUREMENTS ARE THE REFERENCE BUILD'S: 420px wide with a 12px gutter
 * either side on a phone, and a wide, soft shadow rather than Tailwind's
 * stacked default — a sheet in this product sits on the surface, it is not
 * lifted off it. `aria-label` carries the title so the accessible name follows
 * a view swap (the knock dialog is two views under one roof), and `lede`
 * follows it because a sheet that changes what it is showing has to change what
 * it says it is showing.
 *
 * THE TITLE IS NOT SET IN THE DISPLAY FACE, and the reference build's is. The
 * shell does not name a typeface outside the brand skin — `tokens.test.ts` is
 * explicit that the gate is what keeps a 41 KB download off every visit — and a
 * dialog heading is not worth turning that gate off.
 */
export function Sheet({
  title,
  lede = null,
  onClose,
  children,
  wide = false,
}: {
  title: string;
  lede?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  const focus = useFocusTrapEntry();
  return (
    <div className="nob-scrim fixed inset-0 z-50 grid place-items-center p-3">
      <Scrim onClose={onClose} label="Dismiss" />
      <div
        ref={focus}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`relative flex max-h-[min(84vh,44rem)] flex-col rounded-xl border border-border
                    bg-bg text-text shadow-[0_8px_40px_rgba(0,0,0,0.18)] outline-none
                    ${wide ? "w-full max-w-[30rem]" : "w-[min(420px,calc(100vw-24px))]"}`}
      >
        <header className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
            {lede && <div className="mt-1 text-sm leading-snug text-muted">{lede}</div>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 rounded-full p-1 text-muted transition-colors duration-75
                       hover:bg-hover hover:text-text focus-visible:outline-none
                       focus-visible:ring-2 focus-visible:ring-focus/70"
          >
            <CloseIcon className="h-4 w-4" />
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">{children}</div>
      </div>
    </div>
  );
}

/**
 * A row that leads somewhere, in a panel's index.
 *
 * `value` is the current answer, so the index reads as a summary of the whole
 * panel rather than as a menu somebody has to open four times to learn what is
 * set.
 */
/**
 * The shell every row in a plate wears: the same height, the same 12px gutter,
 * the same hairline under everything but the last one. Written once because a
 * plate whose rows disagree about any of the three stops reading as a plate.
 */
const ROW_FRAME = "flex w-full items-center border-b border-border-soft last:border-b-0";
const ROW = `${ROW_FRAME} gap-3 px-3 py-3`;

export function NavRow({
  label,
  value,
  onClick,
  onValueClick,
  valueAriaLabel,
  valueTone,
  valueWrap,
}: {
  label: string;
  value?: string;
  onClick: () => void;
  /**
   * The VALUE is a control too, and a different one from the row (owner item
   * 13, 2026-08-27). "Your door · Paused" reads as one row and is two acts: go
   * and look, and put it back up. When this is given, the row splits into two
   * sibling buttons rather than nesting one inside the other — a button inside
   * a button is not HTML, and a screen reader is entitled to hear two controls
   * where there are two.
   *
   * The chevron stays with the LABEL half, which is the half it points at.
   */
  onValueClick?: () => void;
  valueAriaLabel?: string;
  /**
   * A value that is a WARNING (wave 87): an expired card, or one about to
   * expire, says so in the warning colour.
   */
  valueTone?: "warn";
  /**
   * A value whose END matters (wave 87): the card's expiry is the last thing
   * in "Visa ···· 4242 · expires 08/27", so on a narrow screen it wraps under
   * itself rather than being truncated away, and the label keeps its line.
   */
  valueWrap?: boolean;
}) {
  const nav =
    "text-left transition-colors duration-75 hover:bg-hover focus-visible:outline-none " +
    "focus-visible:ring-2 focus-visible:ring-focus/70";

  // The frame WITHOUT the row's own padding: each half carries its own, so the
  // whole of each half is its hit area rather than a word floating in a gutter
  // that belongs to neither. The order on screen does not change — label,
  // value, chevron — because a row that rearranged itself when its value
  // became pressable would read as a different row arriving.
  if (onValueClick && value) {
    return (
      <div className={ROW_FRAME}>
        <button
          type="button"
          onClick={onClick}
          className={`min-w-0 flex-1 py-3 pl-3 text-[15px] ${nav}`}
        >
          {label}
        </button>
        <button
          type="button"
          aria-label={valueAriaLabel}
          onClick={onValueClick}
          className={`shrink-0 px-3 py-3 text-[13px] text-muted ${nav}`}
        >
          {value}
        </button>
        {/* Decoration: the chevron belongs to the row's destination, and the
            button that goes there already carries the whole label. */}
        <ForwardIcon className="mr-3 h-4 w-4 shrink-0 text-muted" aria-hidden />
      </div>
    );
  }

  return (
    <button type="button" onClick={onClick} className={`${ROW} ${nav}`}>
      <span className={`${valueWrap ? "shrink-0" : "min-w-0 flex-1"} text-[15px]`}>{label}</span>
      {value && (
        <span
          className={`text-[13px] ${valueTone === "warn" ? "text-warn" : "text-muted"} ${
            valueWrap ? "min-w-0 flex-1 text-right" : "max-w-[46%] truncate"
          }`}
          {...(valueTone ? { "data-tone": valueTone } : {})}
        >
          {value}
        </span>
      )}
      <ForwardIcon className="h-4 w-4 shrink-0 text-muted" />
    </button>
  );
}

/**
 * A labelled value on a row. Without `onClick` it is a readout, not a control —
 * the reference build's distinction, and the reason the door's state can sit in
 * the same plate as the things that change it without inviting a press.
 */
export function Row({
  label,
  value,
  onClick,
  ariaLabel,
}: {
  label: string;
  value: ReactNode;
  onClick?: () => void;
  ariaLabel?: string;
}) {
  const body = (
    <>
      <span className="min-w-0 flex-1 text-[13px]">{label}</span>
      <span className="min-w-0 shrink-0 text-right text-[13px] font-medium">{value}</span>
    </>
  );
  if (!onClick) return <div className={`${ROW} items-baseline`}>{body}</div>;
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className={`${ROW} items-baseline text-left transition-colors duration-75 hover:bg-hover
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70`}
    >
      {body}
    </button>
  );
}

/**
 * A titled block inside a panel. The title is a label, never a heading level.
 *
 * THE PLATE IS THE POINT (reference build, 2026-08-25). Everything a settings
 * page says stands on one: a rounded `bg-bg` card, inset from the panel's edge,
 * with the rows contained INSIDE it rather than ruled across the whole column.
 * That is the difference between a settings panel and a scroll of prose, and it
 * is what the panel's own ground — `sidebar`, a colour, not a white — exists to
 * make visible.
 *
 * It insets ITSELF (`mx-3`) rather than relying on a padded container, because
 * two panels host these and only one of them pads. A plate that assumed the
 * padding would be flush to the edge in the other.
 *
 * `data-paper` is what the skin hangs the ink outline and the hard offset
 * shadow on (styles.css) — the same mechanic the desk's cards already use, and
 * the reason a plate on the yellow reads as something printed and laid down.
 */
export function Group({
  title,
  titleId,
  children,
}: {
  title?: string;
  /** Names the section for a screen reader, where the block has its own Save. */
  titleId?: string;
  children: ReactNode;
}) {
  return (
    <section className="mb-4" {...(titleId ? { "aria-labelledby": titleId } : {})}>
      {title && (
        <h3
          {...(titleId ? { id: titleId } : {})}
          className="px-6 pb-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-muted"
        >
          {title}
        </h3>
      )}
      <div data-paper className="mx-3 overflow-hidden rounded-md bg-bg">
        {children}
      </div>
    </section>
  );
}

/**
 * The padding a plate's own contents take when they are NOT a row — a form, a
 * paragraph, a card. Same 12px gutter as a row, so the two stack without a step.
 */
export const PLATE = "px-3 py-3";
